import type { Role, ServerToClient, SignalPayload, Participant } from "@riffbox/protocol";
import { computeTargetPosition } from "@riffbox/clock";
import { genClientId, genRoomCode } from "./ids.js";
import { WsClient, type ConnState } from "./ws.js";
import { SyncEngine } from "./sync.js";
import { attachHls } from "./player.js";
import { AudioMixer } from "./mixer.js";
import { VoiceMesh } from "./webrtc.js";
import { SilhouetteCompositor } from "./compositor.js";

// ─────────────────────────── URL / identity ───────────────────────────
const params = new URLSearchParams(location.search);
const role: Role = params.get("role") === "host" ? "host" : "guest";
let roomId = params.get("room") ?? "";
if (!roomId) roomId = genRoomCode();
const movieSlug = params.get("movie") ?? "sample";
// Stable per-tab identity: survives reloads so the host stays the host
// (first-host-wins keys on clientId) and reconnects keep the same peer id.
const clientId = ((): string => {
  const KEY = "riffbox-clientId";
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = genClientId();
    sessionStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    return genClientId();
  }
})();

// Keep the host's own URL host-authoritative so a reload doesn't demote them to
// a guest. The guest share link is the same URL WITHOUT role (defaults to guest).
if (role === "host") {
  const u = new URL(location.href);
  u.searchParams.set("room", roomId);
  u.searchParams.set("role", "host");
  if (movieSlug !== "sample") u.searchParams.set("movie", movieSlug);
  history.replaceState(null, "", u.toString());
}
function guestShareLink(): string {
  const u = new URL(location.href);
  u.searchParams.set("room", roomId);
  u.searchParams.delete("role"); // guest is the default
  if (movieSlug !== "sample") u.searchParams.set("movie", movieSlug);
  return u.toString();
}

// ─────────────────────────── DOM ───────────────────────────
const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;
const joinScreen = $("join");
const watchScreen = $("watch");
const joinBtn = $<HTMLButtonElement>("joinBtn");
const joinSub = $("join-sub");
const joinHint = $("joinHint");
const optCam = $<HTMLInputElement>("optCam");
const nameInput = $<HTMLInputElement>("nameInput");
const movie = $<HTMLVideoElement>("movie");
const overlay = $<HTMLCanvasElement>("overlay");
const reconnecting = $("reconnecting");
const tapToPlay = $<HTMLButtonElement>("tapToPlay");
const syncing = $("syncing");
const hostControls = $("hostControls");
const playPause = $<HTMLButtonElement>("playPause");
const scrub = $<HTMLInputElement>("scrub");
const timecode = $("timecode");
const shareBtn = $<HTMLButtonElement>("shareBtn");
const guestProgress = $("guestProgress");
const gTime = $("gTime");
const gBar = $<HTMLProgressElement>("gBar");
const gRemain = $("gRemain");
const movieVol = $<HTMLInputElement>("movieVol");
const voiceVol = $<HTMLInputElement>("voiceVol");
const micBtn = $<HTMLButtonElement>("micBtn");
const presenceEl = $("presence");
const syncStat = $("syncStat");

joinSub.textContent =
  role === "host" ? `You're the host · room ${roomId}` : `Joining room ${roomId}`;

// Remember the display name across visits.
try {
  nameInput.value = localStorage.getItem("riffbox-name") ?? "";
} catch {
  /* storage unavailable */
}
let myName = "";

/** Display name for a participant: their chosen name, else a role fallback. */
function nameFor(id: string): string {
  const p = participants.get(id);
  const nm = p?.name?.trim();
  if (nm) return nm;
  return p?.role === "host" ? "Host" : "Guest";
}

// Host-only movie picker (PRD §7): pick from the staged library before joining.
if (role === "host") void setupMoviePicker();
async function setupMoviePicker(): Promise<void> {
  const wrap = document.getElementById("moviePickWrap") as HTMLElement;
  const sel = document.getElementById("moviePick") as HTMLSelectElement;
  let slugs: string[] = [];
  try {
    const res = await fetch("/media/index.json");
    if (res.ok) slugs = (await res.json()) as string[];
  } catch {
    /* no manifest staged yet — leave the picker hidden, ?movie= still works */
  }
  if (!Array.isArray(slugs) || slugs.length === 0) return;
  if (!slugs.includes(movieSlug)) slugs = [movieSlug, ...slugs];
  sel.innerHTML = "";
  for (const s of slugs) {
    const opt = document.createElement("option");
    opt.value = s;
    opt.textContent = s;
    if (s === movieSlug) opt.selected = true;
    sel.appendChild(opt);
  }
  wrap.hidden = false;
  sel.addEventListener("change", () => {
    const u = new URL(location.href);
    u.searchParams.set("room", roomId);
    u.searchParams.set("role", "host");
    u.searchParams.set("movie", sel.value);
    location.href = u.toString(); // reload to re-init against the chosen movie
  });
}

// ─────────────────────────── engines (constructed on join) ───────────────────────────
let mixer: AudioMixer | null = null;
let mesh: VoiceMesh | null = null;
let compositor: SilhouetteCompositor | null = null;
let sync: SyncEngine | null = null;
let ws: WsClient | null = null;
let micOn = false;
// The role the SERVER granted us (room_state.you.role). May differ from the URL
// role if the server downgraded a second "host" claim — controls follow this.
let effectiveRole: Role = role;
const participants = new Map<string, Participant>();

const wsUrl = () => {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}/ws`;
};

// ─────────────────────────── Join (the audio-unlock gesture) ───────────────────────────
joinBtn.addEventListener("click", () => void join());

async function join(): Promise<void> {
  joinBtn.disabled = true;
  joinHint.textContent = "connecting…";

  myName = nameInput.value.trim() || (role === "host" ? "Host" : "Guest");
  try {
    localStorage.setItem("riffbox-name", nameInput.value.trim());
  } catch {
    /* storage unavailable */
  }

  // 1) Audio unlock — must happen inside the user gesture.
  mixer = new AudioMixer();
  await mixer.resume();
  mixer.connectMovie(movie);
  mixer.setMovieVolume(Number(movieVol.value));
  mixer.setVoiceVolume(Number(voiceVol.value));

  // 2) Movie source.
  attachHls(movie, `/media/${movieSlug}/index.m3u8`);
  movie.preservesPitch = true; // keep audio natural during the (rare) rate slews
  // Prime playback within the gesture so autoplay-with-sound is unlocked;
  // the SyncEngine immediately takes over play/pause from the clock.
  movie.muted = false;
  try {
    await movie.play();
  } catch {
    /* sync loop will start it */
  }

  // 3) Optional local media (default mic MUTED — never broadcast room noise on join).
  let local: MediaStream | null = null;
  try {
    // Silhouettes are segmented blobs — low-res cam keeps the (flaky, heaviest)
    // video stream light so it survives a weak uplink. Voice stays full quality.
    local = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: optCam.checked
        ? { width: { ideal: 320 }, height: { ideal: 240 }, frameRate: { ideal: 15 } }
        : false,
    });
    local.getAudioTracks().forEach((t) => (t.enabled = false)); // muted by default
    local.getVideoTracks().forEach((t) => (t.enabled = optCam.checked));
  } catch {
    joinHint.textContent = "joined without mic/cam";
  }

  // 4) WebRTC mesh + silhouette compositor.
  mesh = new VoiceMesh(clientId, {
    sendSignal: (to, payload) => ws?.send({ type: "signal", to, payload }),
    onRemoteStream: (peerId, stream) => {
      mixer?.addVoice(peerId, stream);
      compositor?.addPeer(peerId, stream, nameFor(peerId));
    },
    onPeerGone: (peerId) => {
      mixer?.removeVoice(peerId);
      compositor?.removePeer(peerId);
    },
  });
  mesh.setLocalStream(local);

  compositor = new SilhouetteCompositor(overlay);
  void compositor.init();
  // Show your own silhouette in the strip too (MST3K-style) when sharing cam.
  if (local && local.getVideoTracks().length > 0) compositor.addPeer(clientId, local, myName);

  // 5) Sync engine. Show a one-tap fallback if the browser blocks autoplay.
  sync = new SyncEngine(movie, (m) => ws?.send(m));
  sync.onBlocked = (blocked) => tapToPlay.classList.toggle("hidden", !blocked);

  // 6) Connect.
  ws = new WsClient({
    url: wsUrl(),
    join: { type: "join", roomId, role, clientId, name: myName },
    onMessage: handleMessage,
    onState: setConnState,
    onReconnect: () => {
      sync?.onReconnect();
      mesh?.destroy(); // rebuilt from the fresh room_state
      // Re-announce mic/cam: the server reset our presence on rejoin.
      ws?.send({ type: "presence", micOn, camOn: optCam.checked });
    },
  });
  ws.connect();
  sync.start();

  // 7) UI mode. Size the overlay only once the stage has real dimensions
  // (sizing it while display:none would lock it to the fallback resolution),
  // then start compositing.
  joinScreen.classList.add("hidden");
  watchScreen.classList.remove("hidden");
  if (role === "host") hostControls.classList.remove("hidden");
  else guestProgress.classList.remove("hidden");
  sizeOverlay();
  compositor.start();
}

// ─────────────────────────── Server messages ───────────────────────────
function handleMessage(msg: ServerToClient): void {
  sync?.onMessage(msg);
  switch (msg.type) {
    case "room_state":
      // The server is authoritative about our role; controls follow it.
      effectiveRole = msg.you.role;
      hostControls.classList.toggle("hidden", effectiveRole !== "host");
      guestProgress.classList.toggle("hidden", effectiveRole === "host");
      mesh?.setTurn(msg.turn);
      participants.clear();
      for (const p of msg.participants) {
        participants.set(p.clientId, p);
        mesh?.addPeer(p.clientId);
      }
      participants.forEach((_, id) => compositor?.setName(id, nameFor(id)));
      renderPresence();
      break;
    case "turn_credentials":
      // Apply to LIVE peer connections + ICE-restart, not just stash for new ones.
      void mesh?.refreshTurn(msg.turn);
      break;
    case "peer_joined":
      participants.set(msg.clientId, {
        clientId: msg.clientId,
        role: msg.role,
        micOn: msg.micOn,
        camOn: msg.camOn,
        name: msg.name,
      });
      mesh?.addPeer(msg.clientId);
      compositor?.setName(msg.clientId, nameFor(msg.clientId));
      renderPresence();
      break;
    case "peer_left":
      participants.delete(msg.clientId);
      mesh?.removePeer(msg.clientId);
      renderPresence();
      break;
    case "presence_update": {
      const p = participants.get(msg.clientId);
      if (p) {
        p.micOn = msg.micOn;
        p.camOn = msg.camOn;
        renderPresence();
      }
      break;
    }
    case "clock":
      // Show the sync indicator only while the readiness gate is open.
      if (msg.state !== "preparing") setSyncing(false, []);
      else setSyncing(true, []);
      break;
    case "gate":
      setSyncing(true, msg.waitingOn);
      break;
    case "signal":
      void mesh?.onSignal(msg.from, msg.payload as SignalPayload);
      break;
    case "error":
      // Guests must never see a scary modal; log only.
      console.warn("[riffbox] server error:", msg.code, msg.message);
      break;
    default:
      break;
  }
}

function setConnState(s: ConnState): void {
  reconnecting.classList.toggle("hidden", s === "open" || s === "connecting");
}

function setSyncing(show: boolean, waitingOn: string[]): void {
  syncing.classList.toggle("hidden", !show);
  if (!show) return;
  const names = waitingOn.map((id) => (id === clientId ? "you" : nameFor(id)));
  syncing.textContent = names.length
    ? `Getting everyone in sync — waiting on ${names.join(", ")}`
    : "Getting everyone in sync…";
}

// ─────────────────────────── Host controls ───────────────────────────
let lastKnownState: "playing" | "paused" = "paused";

playPause.addEventListener("click", () => {
  if (effectiveRole !== "host") return;
  const action = lastKnownState === "playing" ? "pause" : "play";
  ws?.send({ type: "control", action, positionSec: movie.currentTime });
});

let lastScrubSentMs = 0;
function sendScrubSeek(): void {
  if (effectiveRole !== "host" || !movie.duration) return;
  const target = (Number(scrub.value) / 100) * movie.duration;
  ws?.send({ type: "control", action: "seek", positionSec: target });
}
// Throttle while dragging so a scrub doesn't flood seeks; always send the final.
scrub.addEventListener("input", () => {
  const now = Date.now();
  if (now - lastScrubSentMs < 180) return;
  lastScrubSentMs = now;
  sendScrubSeek();
});
scrub.addEventListener("change", sendScrubSeek);

shareBtn.addEventListener("click", async () => {
  const link = guestShareLink();
  try {
    await navigator.clipboard.writeText(link);
    shareBtn.textContent = "✓ copied";
  } catch {
    // Clipboard blocked (no gesture/permission): fall back to showing the link.
    shareBtn.textContent = link;
  }
  setTimeout(() => (shareBtn.textContent = "🔗 share"), 2500);
});

tapToPlay.addEventListener("click", () => {
  // A real user gesture re-activates the element; the sync loop keeps it going.
  void mixer?.resume();
  void movie.play().catch(() => {});
  tapToPlay.classList.add("hidden");
});

// ─────────────────────────── Mixers / mic ───────────────────────────
movieVol.addEventListener("input", () => mixer?.setMovieVolume(Number(movieVol.value)));
voiceVol.addEventListener("input", () => mixer?.setVoiceVolume(Number(voiceVol.value)));
micBtn.addEventListener("click", () => {
  micOn = !micOn;
  mesh?.setMicEnabled(micOn);
  micBtn.textContent = micOn ? "🎙️ on" : "🎙️ off";
  micBtn.className = micOn ? "mic-on" : "mic-off";
  ws?.send({ type: "presence", micOn, camOn: optCam.checked });
});

// ─────────────────────────── UI loop ───────────────────────────
function renderPresence(): void {
  const others = [...participants.values()];
  presenceEl.textContent =
    `${others.length + 1} watching` + (others.length ? ` · ${others.map((p) => (p.micOn ? "🎙️" : "🔇")).join(" ")}` : "");
}

setInterval(() => {
  const dur = movie.duration;
  if (effectiveRole === "host") {
    if (dur && document.activeElement !== scrub) {
      // Don't yank the thumb out from under the host while they're scrubbing.
      scrub.value = String((movie.currentTime / dur) * 100);
    }
    playPause.textContent = movie.paused ? "▶︎" : "⏸";
    timecode.textContent = dur ? `${fmt(movie.currentTime)} / ${fmt(dur)}` : fmt(movie.currentTime);
  } else {
    // Read-only progress for guests.
    gTime.textContent = fmt(movie.currentTime);
    if (dur && isFinite(dur)) {
      gBar.value = (movie.currentTime / dur) * 100;
      gRemain.textContent = `-${fmt(dur - movie.currentTime)}`;
    }
  }
  if (sync?.lastTick) {
    const d = sync.lastTick.targetSec - movie.currentTime;
    syncStat.textContent = `Δ ${(d * 1000).toFixed(0)}ms · off ${sync.estimatedOffsetMs().toFixed(0)}ms`;
    lastKnownState = sync.lastTick.paused ? "paused" : "playing";
  }
}, 250);

/** Format seconds as M:SS, or H:MM:SS for movie-length durations. */
function fmt(sec: number): string {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const total = Math.floor(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = s.toString().padStart(2, "0");
  return h > 0 ? `${h}:${m.toString().padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

function sizeOverlay(): void {
  overlay.width = overlay.clientWidth || 1280;
  overlay.height = overlay.clientHeight || 720;
}
window.addEventListener("resize", sizeOverlay);

// Expose a tiny surface for the e2e proxies to assert against.
(window as unknown as { __riffboxClientId: string }).__riffboxClientId = clientId;
(window as unknown as { __riffbox: unknown }).__riffbox = {
  get offsetMs() {
    return sync?.estimatedOffsetMs() ?? null;
  },
  get target() {
    return sync?.lastTick?.targetSec ?? null;
  },
  get currentTime() {
    return movie.currentTime;
  },
  get audioState() {
    return mixer?.state ?? null;
  },
  get movieGain() {
    return mixer?.movieGainValue ?? null;
  },
  get connState() {
    return ws?.connectionState ?? null;
  },
  get paused() {
    return movie.paused;
  },
  get peerIds() {
    return mesh?.peerIds() ?? [];
  },
  // Test seams for the e2e proxies (drive the clock deterministically,
  // independent of whether the headless browser can decode the sample codec).
  dropWs: () => ws?.dropForTest(),
  duck: () => mixer?.noteSpeech(),
  hostPlay: () => ws?.send({ type: "control", action: "play", positionSec: movie.currentTime }),
  hostPause: () => ws?.send({ type: "control", action: "pause", positionSec: movie.currentTime }),
  hostSeek: (sec: number) => ws?.send({ type: "control", action: "seek", positionSec: sec }),
  peerStats: async (peerId: string) => {
    const pc = mesh?.peerConnection(peerId);
    if (!pc) return null;
    const stats = await pc.getStats();
    type Cand = { id: string; candidateType?: string };
    const local = new Map<string, Cand>();
    const remote = new Map<string, Cand>();
    let pair: { state: string; localType?: string; remoteType?: string } | null = null;
    let bytesReceived = 0;
    stats.forEach((r) => {
      if (r.type === "local-candidate") local.set(r.id, r as unknown as Cand);
      else if (r.type === "remote-candidate") remote.set(r.id, r as unknown as Cand);
    });
    stats.forEach((r) => {
      if (r.type === "candidate-pair") {
        const cp = r as RTCIceCandidatePairStats;
        if (cp.nominated) {
          pair = {
            state: cp.state,
            localType: local.get(cp.localCandidateId ?? "")?.candidateType,
            remoteType: remote.get(cp.remoteCandidateId ?? "")?.candidateType,
          };
        }
      } else if (r.type === "inbound-rtp") {
        bytesReceived += (r as RTCInboundRtpStreamStats).bytesReceived ?? 0;
      }
    });
    return { connectionState: pc.connectionState, iceConnectionState: pc.iceConnectionState, pair, bytesReceived };
  },
  computeTargetPosition,
};
